import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { demoCallsApi } from "./api"
import type { DemoCallRequest, DemoCallState } from "./types"
import { timestampMs } from "@/lib/datetime"

export const demoCallKeys = {
  options: ["demo-calls", "options"] as const,
  recent: ["demo-calls", "recent"] as const,
  status: (callId: string) => ["demo-calls", "status", callId] as const,
}

/** How long to keep waiting for the post-call analysis after a call ends. */
const ANALYSIS_GRACE_MS = 90_000

/** Terminal states — once reached, stop polling. */
const SETTLED: DemoCallState[] = [
  "COMPLETED",
  "VOICEMAIL",
  "FAILED",
  "CANCELLED",
]

export function useDemoCallOptions() {
  return useQuery({
    queryKey: demoCallKeys.options,
    queryFn: demoCallsApi.getOptions,
    staleTime: 5 * 60 * 1000,
  })
}

export function useRecentDemoCalls() {
  return useQuery({
    queryKey: demoCallKeys.recent,
    queryFn: () => demoCallsApi.getRecent(8),
    staleTime: 30 * 1000,
  })
}

/**
 * Follows one demo call from dial to transcript.
 *
 * Polls every 3s while live; the analysis (summary, sentiment) lands on a
 * later `call_analyzed` webhook, so keep polling a short while after the call
 * ends rather than stopping the moment the status settles.
 */
export function useDemoCallStatus(callId: string | null) {
  return useQuery({
    queryKey: demoCallKeys.status(callId ?? ""),
    queryFn: () => demoCallsApi.getStatus(callId as string),
    enabled: !!callId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 3000
      if (!SETTLED.includes(data.status)) return 3000
      if (data.summary) return false
      // Settled but no analysis yet. Retell sends `call_analyzed` a few seconds
      // after a real conversation — but never for a call that failed to
      // connect, so give up rather than polling forever.
      const endedAt = data.ended_at ?? data.created_at
      const waitedMs = endedAt ? Date.now() - timestampMs(endedAt) : 0
      return waitedMs > ANALYSIS_GRACE_MS ? false : 5000
    },
    staleTime: 0,
  })
}

export function usePlaceDemoCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: DemoCallRequest) => demoCallsApi.placeCall(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: demoCallKeys.recent })
    },
  })
}
